"use client";

import { Bot, ShieldAlert } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import { InsuraBot } from "@/app/_module/components/InsuraBot";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";

import CreateResourceModal from "./_modules/components/createModal";
import EditResourceModal from "./_modules/components/editModal";
import { ResourceCenter } from "./_modules/components/ResourceCenter";
import type { ColumnDef } from "./_modules/components/ResourceManagement";
import ResourceManagementTable from "./_modules/components/ResourceManagement";
import {
  useCreateResource,
  useDeleteResource,
  useResources,
  useUnlikeResource,
  useUpdateResource,
  useTrackDownload,
  useSubAgencyIds,
} from "./_modules/hooks/use-hook";
import { resourceSchema } from "./_modules/schema";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";
import type {
  CreateResourceRequest,
  ResourceType,
  UpdateResourceRequest,
} from "./_modules/types/types";

const Resources = function () {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [isBotOpen, setIsBotOpen] = useState(false);

  // Get initial tab from URL or default to "browse"
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'browse';
  });

  // Handle tab change - update URL to persist tab selection
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Sync tab state with URL on initial load and URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const { data: resourceData, isLoading } = useResources();
  const { user } = useUserContext();
  const { branding } = useBranding();
  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();
  const unlikeMutation = useUnlikeResource();
  const trackDownloadMutation = useTrackDownload();

  // Check if user is agency owner
  const isAgencyOwner = user?.user?.userType === 'agency_owner' || user?.currentAgency?.role === 'agency_owner';

  // Get current user ID and agency ID for edit/delete permission checks
  const currentUserId = user?.user?.id;
  const currentAgencyId = user?.user?.agencyId || user?.currentAgency?.agency?.id;

  // Get sub-agency IDs for hierarchical permission checks
  // Agency owners can edit/delete resources from their agency AND all sub-agencies below
  const { data: subAgencyIds = [] } = useSubAgencyIds();

  const handleCreate = (
    data: CreateResourceRequest,
    reset: () => void,
    close: () => void
  ) => {
    if (!data.file) {
      throw new Error("File is required");
    }

    createMutation.mutate(data, {
      onSuccess: () => {
        reset();
        close();
      },
    });
  };

  const handleUpdate = (
    resourceId: string,
    data: UpdateResourceRequest,
    close: () => void
  ) => {
    updateMutation.mutate(
      { id: resourceId, data },
      {
        onSuccess: () => {
          close();
        },
      }
    );
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleUnlike = async (id: string) => {
    unlikeMutation.mutate(id);
  };

  // Enhanced download handler for management table
  const handleDownloadFromTable = async (resource: ResourceType) => {
    const primaryFileUrl = resource.fileUrl || ((resource as unknown as { uploads?: { fileUrl?: string }[] }).uploads?.[0]?.fileUrl ?? "");
    if (!primaryFileUrl) {
      return;
    }

    try {
      // Track the download
      trackDownloadMutation.mutate(resource.id);

      // Open the file in a new tab for download
      const link = document.createElement('a');
      link.href = primaryFileUrl;
      link.download = `${resource.title}.${resource.fileType.toLowerCase()}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {

    }
  };

  const columns: ColumnDef<ResourceType>[] = [
    {
      key: "title",
      label: "Title",
      render: (res) => (
        <div className="flex items-center gap-2">
          <span>{res.title}</span>
          {res.isSensitive && (
            <span title={res.autoDetectedSensitive ? "Auto-detected sensitive content" : "Marked as sensitive"}>
              <ShieldAlert className="h-4 w-4 text-amber-600" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: "fileType",
      label: "File Type",
      render: (res) => <Badge>{res.fileType}</Badge>,
    },
    { key: "carrier", label: "Carrier" },
    {
      key: "scope",
      label: "Visibility",
      render: (res) => {
        const scopeConfig: Record<string, { label: string; className: string }> = {
          'personal': { label: 'Personal', className: 'bg-gray-100 text-gray-700' },
          'agency-only': { label: 'My Agency', className: 'bg-primary/10 text-primary' },
          'agency-and-below': { label: 'Agency + Subs', className: 'bg-secondary/10 text-secondary' },
          'agency-wide': { label: 'Agency-Wide', className: 'bg-primary/10 text-primary' },
        };
        const config = scopeConfig[res.scope || 'personal'] || scopeConfig['personal'];

        return (
          <div className="flex items-center gap-1 flex-wrap">
            <Badge className={config.className} variant="secondary">
              {config.label}
            </Badge>
            {res.isSensitive && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="secondary">
                Sensitive
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "uploadedBy",
      label: "Uploaded By",
      render: (res) => res.uploadedBy ?? "Unknown",
    },
    {
      key: "downloadCount",
      label: "Downloads",
      render: (res) => (
        <div className="flex items-center gap-2">
          <span>{res.downloadCount ?? 0}</span>
          {(res.downloadCount ?? 0) > 10 && (
            <Badge className="text-xs" variant="secondary">
              Popular
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "likeCount",
      label: "Likes",
      render: (res) => (
        <div className="flex items-center gap-2">
          <span>{res.likeCount ?? 0}</span>
          {!!res.isLiked && <span className="text-red-500">❤️</span>}
        </div>
      ),
    },
    { 
      key: "actions", 
      label: "Actions",
      render: (res) => (
        <div className="flex items-center gap-2">
          <Button
            disabled={!res.fileUrl && !((res as unknown as { uploads?: { fileUrl?: string }[] }).uploads?.[0]?.fileUrl)}
            onClick={() => handleDownloadFromTable(res)}
            size="sm"
            variant="outline"
          >
            Download
          </Button>
        </div>
      ),
    },
  ];

  // Filter liked resources for favorites tab
  const favoriteResources =
    resourceData?.filter((resource) => resource.isLiked) ?? [];

  // Get stats for the dashboard
  const totalResources = resourceData?.length ?? 0;
  const totalDownloads = (resourceData || []).reduce((sum, resource) => sum + (resource.downloadCount ?? 0), 0);
  const totalLikes = (resourceData || []).reduce((sum, resource) => sum + (resource.likeCount ?? 0), 0);
  const mostDownloaded = (resourceData || []).reduce((prev, current) =>
    (current.downloadCount ?? 0) > (prev.downloadCount ?? 0) ? current : prev,
    (resourceData || [])[0]
  );

  // Determine header title based on agency status
  // For independent agents (no agency), show "{brandName} Resource Center"
  // For agency agents, show "{Agency Name} Resource Center"
  const resourceCenterTitle = user?.currentAgency?.agency?.name
    ? `${user.currentAgency.agency.name} Resource Center`
    : `${branding.brandName} Resource Center`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between" data-tour="resources-header">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {resourceCenterTitle}
          </h1>
          <p className="text-muted-foreground mt-1">
            Access carrier forms, documents, and resources with AI assistance
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span>{totalResources} resources</span>
            <span>{totalDownloads} total downloads</span>
            <span>{favoriteResources.length} favorites</span>
          </div>
        </div>
      </div>

      <Tabs className="w-full" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="inline-flex w-full max-w-2xl" data-tour="resources-tabs">
          <TabsTrigger className="flex-1 text-xs sm:text-sm px-2 sm:px-3" value="browse" data-tour="resources-tab-browse">Browse</TabsTrigger>
          <TabsTrigger className="flex-1 text-xs sm:text-sm px-2 sm:px-3" value="favorites" data-tour="resources-tab-favorites">
            Favorites{" "}
            {favoriteResources.length > 0 && `(${favoriteResources.length})`}
          </TabsTrigger>
          <TabsTrigger className="flex-1 text-xs sm:text-sm px-2 sm:px-3" value="resource" data-tour="resources-tab-management">Management</TabsTrigger>
        </TabsList>

        <TabsContent className="space-y-4" value="browse">
          <ResourceCenter
            renderCreateButton={() => (
              <ViewerDisabled message="Upgrade to create resources">
                <CreateResourceModal
                  loading={createMutation.isPending}
                  onCreate={handleCreate}
                  title="Create Resource"
                  description="Upload and organize your resource files"
                  schema={resourceSchema}
                  uploadProgress={0}
                />
              </ViewerDisabled>
            )}
          />
        </TabsContent>

        <TabsContent className="space-y-4" value="favorites">
          <div className="bg-card rounded-lg shadow-sm border p-6">
            {favoriteResources.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Your Favorite Resources</h3>
                  <Badge variant="outline">
                    {favoriteResources.length} favorite{favoriteResources.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {favoriteResources.map((resource) => (
                    <div key={resource.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h4 className="font-medium mb-2">{resource.title}</h4>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {resource.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Badge variant="outline">{resource.fileType}</Badge>
                        {(resource.downloadCount ?? 0) > 0 && (
                          <Badge className="text-xs" variant="secondary">
                            {resource.downloadCount} downloads
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <div className="flex gap-2">
                          {/* Only show View button for PDFs */}
                          {resource.fileType?.toLowerCase() === 'pdf' && (
                            <Button
                              disabled={!resource.fileUrl}
                              onClick={async () => {
                                const backendUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';

                                try {
                                  // Get the auth token from Supabase session (proper method)
                                  const accessToken = await supabaseAuthService.getAccessToken();

                                  if (!accessToken) {
                                    console.error('No access token available');
                                    return;
                                  }

                                  const viewUrl = `${backendUrl}/api/resources/${resource.id}/view`;

                                  const response = await fetch(viewUrl, {
                                    headers: {
                                      'Authorization': `Bearer ${accessToken}`,
                                    },
                                    credentials: 'include',
                                  });

                                  if (!response.ok) {
                                    console.error('View request failed:', response.status, response.statusText);
                                    throw new Error(`Failed to fetch file: ${response.status}`);
                                  }

                                  const blob = await response.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  window.open(blobUrl, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                                } catch (error) {
                                  console.error('View error:', error);
                                }
                              }}
                              size="sm"
                              variant="outline"
                            >
                              View
                            </Button>
                          )}
                          <Button
                            disabled={!resource.fileUrl}
                            onClick={() => handleDownloadFromTable(resource)}
                            size="sm"
                            variant="outline"
                          >
                            Download
                          </Button>
                        </div>
                        <Button
                          className="text-red-500 hover:text-red-600"
                          disabled={unlikeMutation.isPending}
                          onClick={() => handleUnlike(resource.id)}
                          size="sm"
                          variant="ghost"
                        >
                          ❤️ Unlike
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Your favorite resources will appear here. Click the heart icon
                  on any document to save it.
                </p>
                {/* FIXED: Use state instead of DOM manipulation */}
                <Button onClick={() => setActiveTab("browse")}>
                  Browse Resources
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent className="space-y-4" value="resource">
          <ResourceManagementTable
            columns={columns}
            data={resourceData ?? []}
            isLoading={isLoading}
            onDelete={handleDelete}
            canDelete={(resource) => {
              // User can delete if they created it
              if (resource.createdBy === currentUserId) return true;
              // Agency owners can delete resources from their own agency AND sub-agencies
              // They cannot delete shared resources from parent agencies
              if (isAgencyOwner && resource.agencyId) {
                // Check if resource belongs to user's agency or any sub-agency
                const isOwnAgency = resource.agencyId === currentAgencyId;
                const isSubAgency = subAgencyIds.includes(resource.agencyId);
                if (isOwnAgency || isSubAgency) return true;
              }
              return false;
            }}
            title="Manage Resources"
            renderCreateModal={() => (
              <ViewerDisabled message="Upgrade to create resources">
                <CreateResourceModal
                  loading={createMutation.isPending}
                  onCreate={handleCreate}
                  title="Create Resource"
                  description="Upload and organize your resource files"
                  schema={resourceSchema}
                  uploadProgress={0}
                />
              </ViewerDisabled>
            )}
            renderEditModal={(resource) => {
              // User can edit if they created it
              const isCreator = resource.createdBy === currentUserId;
              // Agency owners can edit resources from their own agency AND sub-agencies
              // They cannot edit shared resources from parent agencies
              let canOwnerEdit = false;
              if (isAgencyOwner && resource.agencyId) {
                const isOwnAgency = resource.agencyId === currentAgencyId;
                const isSubAgency = subAgencyIds.includes(resource.agencyId);
                canOwnerEdit = isOwnAgency || isSubAgency;
              }

              if (!isCreator && !canOwnerEdit) {
                return null;
              }
              return (
                <EditResourceModal
                  loading={updateMutation.isPending}
                  onEdit={handleUpdate}
                  resource={resource}
                />
              );
            }}
          />
        </TabsContent>
      </Tabs>

      <InsuraBot
        contextMetadata={{
          availableResources: resourceData?.map((doc: any) => ({
            id: doc.id,
            title: doc.title,
            description: doc.description,
            carrier: doc.carrier?.name || doc.carrierName,
            category: doc.category?.name || doc.categoryName,
            categoryId: doc.categoryId,
            fileType: doc.fileType,
            tags: doc.tags || [],
            url: doc.fileUrl || doc.url
          })) || []
        }}
        defaultContext="resources"
        onOpenChange={setIsBotOpen}
        open={isBotOpen}
        // ⚠️ Secondary InsuraBot mount: on the shell, mirroring open into the
        // shared flag drags the layout's nav bot open ON TOP of this one
        // (see the quote-tab ghost-instance bug). Desktop keeps the mirror.
        syncOpenToContext={
          !(typeof window !== "undefined" && window.location.pathname.startsWith("/m"))
        }
      />
    </div>
  );
};

export default Resources;